import { Controller } from "@hotwired/stimulus"

// Gates a destructive submit behind typing the record's name back.
//
// The affordance, not the guard: `Events::DeleteEvent` verifies the same
// string server-side and refuses without it, so this exists to give somebody
// somewhere to type — and to keep the button inert until what they typed
// matches, rather than letting them submit and bounce off the validation.
//
// Reaches its button by id rather than by target, the same way
// `scope-choice-modal` does: the sheet's footer is rendered outside the form,
// so the submit is not a descendant of this controller's element.
//
// Comparison mirrors the interaction's `normalize`
// (`value.to_s.strip.squeeze(' ').downcase`): neither casing nor a doubled
// space is what the confirmation is testing for, and somebody copying the name
// out of the heading above picks both up without meaning to.
//
// Usage:
//   <form data-controller="confirm-by-name"
//         data-confirm-by-name-expected-value="Tuesday Lunch"
//         data-confirm-by-name-button-id-value="delete_event_submit_button">
//     <input data-confirm-by-name-target="input"
//            data-action="input->confirm-by-name#check">
//   </form>
//   <button id="delete_event_submit_button" disabled>Delete</button>
export default class extends Controller<HTMLElement> {
  static targets = ["input"]
  static values = {
    expected: String,
    buttonId: String,
  }

  declare readonly inputTarget: HTMLInputElement
  declare readonly hasInputTarget: boolean
  declare expectedValue: string
  declare buttonIdValue: string

  connect(): void {
    this.check()
  }

  check(): void {
    const button = this.submitButton
    if (!button) return

    button.disabled = !this.matches()
  }

  private get submitButton(): HTMLButtonElement | null {
    if (!this.buttonIdValue) return null

    return document.getElementById(this.buttonIdValue) as HTMLButtonElement | null
  }

  private matches(): boolean {
    if (!this.hasInputTarget) return false

    return this.normalize(this.inputTarget.value) === this.normalize(this.expectedValue)
  }

  private normalize(value: string): string {
    return String(value ?? "")
      .trim()
      .replace(/ +/g, " ")
      .toLowerCase()
  }
}
